import React from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { ChevronUpDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import { cn } from '../../utils/cn';
import { generateId, handleArrowNavigation } from '../../utils/accessibility';

export interface SelectOption {
  label: string;
  value: string;
  disabled?: boolean;
  description?: string;
}

interface SelectProps {
  options: SelectOption[];
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  error?: string;
  helperText?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  id?: string;
  'aria-describedby'?: string;
}

export const Select: React.FC<SelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  label,
  error,
  helperText,
  disabled = false,
  required = false,
  className,
  id,
  'aria-describedby': ariaDescribedBy,
}) => {
  const selectId = id || generateId('select');
  const errorId = error ? `${selectId}-error` : undefined;
  const helperTextId = helperText ? `${selectId}-helper` : undefined;

  const describedBy =
    [ariaDescribedBy, errorId, helperTextId].filter(Boolean).join(' ') ||
    undefined;

  const selectedOption = options.find((option) => option.value === value);

  return (
    <Listbox value={value} onChange={onChange} disabled={disabled}>
      <div className={cn('space-y-1', className)}>
        {label && (
          <Listbox.Label
            as="label"
            className="block text-sm font-medium text-gray-300"
          >
            {label}
            {required && (
              <span className="text-error-400 ml-1" aria-label="required">
                *
              </span>
            )}
          </Listbox.Label>
        )}

        <div className="relative">
          <Listbox.Button
            id={selectId}
            className={cn(
              'relative w-full cursor-default rounded-md border border-gray-600 bg-gray-800 py-2 pl-3 pr-10 text-left text-sm text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50 transition-colors',
              error && 'border-error-500 focus:ring-error-500'
            )}
            aria-describedby={describedBy}
            aria-invalid={error ? 'true' : 'false'}
            aria-required={required}
          >
            <span className="block truncate">
              {selectedOption ? (
                selectedOption.label
              ) : (
                <span className="text-gray-400">{placeholder}</span>
              )}
            </span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon
                className="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </span>
          </Listbox.Button>

          <Transition
            as={React.Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-gray-800 border border-gray-600 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
              {options.length === 0 ? (
                <div className="px-4 py-2 text-gray-400 text-sm">
                  No options available
                </div>
              ) : (
                options.map((option) => (
                  <Listbox.Option
                    key={option.value}
                    className={({ active, disabled: optionDisabled }) =>
                      cn(
                        'relative cursor-default select-none py-2 pl-10 pr-4',
                        active && !optionDisabled
                          ? 'bg-primary-600 text-white'
                          : 'text-gray-100',
                        optionDisabled && 'opacity-50 cursor-not-allowed'
                      )
                    }
                    value={option.value}
                    disabled={option.disabled}
                  >
                    {({ selected, active }) => (
                      <>
                        <div className="flex flex-col">
                          <span
                            className={cn(
                              'block truncate',
                              selected ? 'font-medium' : 'font-normal'
                            )}
                          >
                            {option.label}
                          </span>
                          {option.description && (
                            <span
                              className={cn(
                                'text-xs mt-1',
                                active ? 'text-white/80' : 'text-gray-400'
                              )}
                            >
                              {option.description}
                            </span>
                          )}
                        </div>
                        {selected ? (
                          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary-400">
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Listbox.Option>
                ))
              )}
            </Listbox.Options>
          </Transition>
        </div>

        {error && (
          <p
            id={errorId}
            className="text-sm text-error-400"
            role="alert"
            aria-live="polite"
          >
            {error}
          </p>
        )}

        {helperText && !error && (
          <p id={helperTextId} className="text-sm text-gray-400">
            {helperText}
          </p>
        )}
      </div>
    </Listbox>
  );
};
